import type { ChronologyEvent } from '@/hooks/useChronology';
import type { RegionalDateLocale } from '@/utils/date-utils';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parse a `Date` or ISO/`YYYY-MM-DD` string into a local-midnight `Date`.
 *
 * Avoids the UTC-midnight -> wrong-local-day shift that `new Date("YYYY-MM-DD")`
 * produces for users west/east of UTC (which can flip the displayed day and
 * "overdue" status by one). A `Date` input is normalised to local midnight so
 * callers get a stable, time-free day value.
 */
export function toLocalDate(date: Date | string): Date {
  if (date instanceof Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  const [y, m, d] = date.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

type Precision = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';
type TimePrecision = Extract<Precision, 'hour' | 'minute' | 'second'>;

function formatBound(
  dt: Date,
  precision: Precision,
  includeYear: boolean,
  locale: RegionalDateLocale,
): string {
  const year = dt.getUTCFullYear();
  const month = MONTHS[dt.getUTCMonth()];
  const hh = dt.getUTCHours().toString().padStart(2, '0');
  const mm = dt.getUTCMinutes().toString().padStart(2, '0');
  const ss = dt.getUTCSeconds().toString().padStart(2, '0');
  const dayLabel = formatDayLabel(dt, includeYear, locale);

  switch (precision) {
    case 'year':
      return `${year}`;
    case 'month':
      return includeYear ? `${month} ${year}` : month;
    case 'day':
      return dayLabel;
    case 'hour':
      return `${dayLabel} ${hh}:00`;
    case 'minute':
      return `${dayLabel} ${hh}:${mm}`;
    case 'second':
      return `${dayLabel} ${hh}:${mm}:${ss}`;
  }
}

function formatDayLabel(dt: Date, includeYear: boolean, locale: RegionalDateLocale): string {
  const year = dt.getUTCFullYear();
  const month = MONTHS[dt.getUTCMonth()];
  const day = dt.getUTCDate();
  return includeYear
    ? locale === 'us' ? `${month} ${day}, ${year}` : `${day} ${month} ${year}`
    : locale === 'us' ? `${month} ${day}` : `${day} ${month}`;
}

function formatTimeLabel(dt: Date, precision: TimePrecision): string {
  const hh = dt.getUTCHours().toString().padStart(2, '0');
  const mm = dt.getUTCMinutes().toString().padStart(2, '0');
  const ss = dt.getUTCSeconds().toString().padStart(2, '0');

  switch (precision) {
    case 'hour':
      return `${hh}:00`;
    case 'minute':
      return `${hh}:${mm}`;
    case 'second':
      return `${hh}:${mm}:${ss}`;
  }
}

function hasTimePrecision(precision: Precision): precision is TimePrecision {
  return precision === 'hour' || precision === 'minute' || precision === 'second';
}

function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate()
  );
}

/**
 * Render an event's temporal label compactly. When the year matches the
 * surrounding section header (`contextYear`), the year is dropped from the
 * label since the header already conveys it. If the event spans into a
 * different year, the year is preserved on the bound that diverges.
 *
 * For a year-precision event whose year matches the context, returns null
 * so the caller can hide the per-event label entirely (the section header
 * carries the placement).
 */
export function formatTemporalCompact(
  ev: Pick<ChronologyEvent,
    'temporal_lower' | 'temporal_upper' |
    'temporal_lower_inf' | 'temporal_upper_inf' |
    'start_precision' | 'end_precision' |
    'temporal_display'
  >,
  contextYear: string | null,
  locale: RegionalDateLocale = 'uk',
): string | null {
  const lower = ev.temporal_lower;
  const upper = ev.temporal_upper;
  const startPrec = ev.start_precision;
  const endPrec = ev.end_precision;

  // Open-ended start ("Until X")
  if (ev.temporal_lower_inf && upper && endPrec) {
    const upperYear = upper.getUTCFullYear().toString();
    const stripYear = contextYear !== null && contextYear === upperYear;
    return `Until ${formatBound(upper, endPrec, !stripYear, locale)}`;
  }

  // Open-ended end ("Since X")
  if (ev.temporal_upper_inf && lower && startPrec) {
    const lowerYear = lower.getUTCFullYear().toString();
    const stripYear = contextYear !== null && contextYear === lowerYear;
    return `Since ${formatBound(lower, startPrec, !stripYear, locale)}`;
  }

  if (!lower || !startPrec) {
    return ev.temporal_display ?? null;
  }

  const lowerYear = lower.getUTCFullYear().toString();
  const lowerStripYear = contextYear !== null && contextYear === lowerYear;

  // Single instant — both bounds equal at same precision
  const isInstant =
    upper &&
    endPrec === startPrec &&
    lower.getTime() === upper.getTime() - precisionMs(startPrec);

  if (isInstant || !upper || !endPrec) {
    if (startPrec === 'year' && lowerStripYear) return null;
    return formatBound(lower, startPrec, !lowerStripYear, locale);
  }

  // Range — inclusive upper-bound (upper minus one precision unit)
  const upperInclusive = new Date(upper.getTime() - precisionMs(endPrec));
  const upperYear = upperInclusive.getUTCFullYear().toString();
  const upperStripYear = contextYear !== null && contextYear === upperYear && lowerStripYear;

  if (hasTimePrecision(startPrec) && hasTimePrecision(endPrec) && isSameUtcDay(lower, upperInclusive)) {
    const dayLabel = formatDayLabel(lower, !lowerStripYear, locale);
    const lowerTime = formatTimeLabel(lower, startPrec);
    const upperTime = formatTimeLabel(upperInclusive, endPrec);
    return `${dayLabel} ${lowerTime}-${upperTime}`;
  }

  const lowerStr = formatBound(lower, startPrec, !lowerStripYear, locale);
  const upperStr = formatBound(upperInclusive, endPrec, !upperStripYear, locale);
  if (lowerStr === upperStr) return lowerStr;
  return `${lowerStr} to ${upperStr}`;
}

function precisionMs(p: Precision): number {
  switch (p) {
    case 'second': return 1_000;
    case 'minute': return 60_000;
    case 'hour':   return 3_600_000;
    case 'day':    return 86_400_000;
    // For month/year the exclusive upper isn't a fixed offset, but we only
    // hit this path when comparing single instants where lower==upper
    // already, so any non-zero ms suffices to detect a non-instant range.
    case 'month':  return 28 * 86_400_000;
    case 'year':   return 365 * 86_400_000;
  }
}
